

import {
  Business as Building2,
  Event as Calendar,
  LocationOn as MapPin,
  Search,
  Person as User,
  Bolt as Zap
} from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';

import { EnrichedEntity } from '@/lib/types';
import { EntityGroup } from './EntityGroup';


interface EntitiesDrawerProps {
  isOpen: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  characters?: EnrichedEntity[];
  locations?: EnrichedEntity[];
  organizations?: EnrichedEntity[];
  events?: EnrichedEntity[];
  arcs?: EnrichedEntity[];
  onSelectEntity: (entity: EnrichedEntity, event: React.MouseEvent) => void;
  onClose: () => void;
  onEntityDragStart?: (entity: EnrichedEntity, event: React.DragEvent) => void;
}


export function EntitiesDrawer({
  isOpen,
  search,
  onSearchChange,
  characters = [],
  locations = [],
  organizations = [],
  events = [],
  arcs = [],
  onSelectEntity,
  onClose,
  onEntityDragStart,
}: EntitiesDrawerProps) {
  const { t } = useTranslation();

  // Filter entities by search
  const filterBySearch = <T extends EnrichedEntity>(items: T[]) => {
    if (!search) return items;
    return items.filter(item =>
      (item.name || (item as { title?: string }).title || '').toLowerCase().includes(search.toLowerCase())
    );

  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        onClose={onClose}
        anchor="right"
        open={isOpen}
        PaperProps={{ sx: { width: isMobile ? '90vw' : 360, overflow: 'hidden' } }}
        sx={{
          width: isOpen ? 360 : 0,
          flexShrink: 0,
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: isMobile ? '90vw' : 360,
            boxSizing: 'border-box',
            position: isMobile ? undefined : 'relative',
            borderLeft: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{
            p: 1.5,
            pl: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('editor.entities')}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Search */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('editor.search_entities')}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>

          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <List dense>
              <EntityGroup
                title={t('editor.characters')}
                items={filterBySearch(characters)}
                icon={<User sx={{ fontSize: 18 }} />}
                onSelect={onSelectEntity}
                onDragStart={onEntityDragStart}
              />
              <EntityGroup
                title={t('editor.locations')}
                items={filterBySearch(locations)}
                icon={<MapPin sx={{ fontSize: 18 }} />}
                onSelect={onSelectEntity}
                onDragStart={onEntityDragStart}
              />
              <EntityGroup
                title={t('editor.organizations')}
                items={filterBySearch(organizations)}
                icon={<Building2 sx={{ fontSize: 18 }} />}
                onSelect={onSelectEntity}
                onDragStart={onEntityDragStart}
              />
              <EntityGroup
                title={t('editor.events')}
                items={filterBySearch(events)}
                icon={<Calendar sx={{ fontSize: 18 }} />}
                onSelect={onSelectEntity}
                onDragStart={onEntityDragStart}
              />
              <EntityGroup
                title={t('editor.arcs')}
                items={filterBySearch(arcs)}
                icon={<Zap sx={{ fontSize: 18 }} />}
                onSelect={onSelectEntity}
                onDragStart={onEntityDragStart}
              />

            </List>
          </Box>
        </Box>
      </Drawer>
  );
}
